import { useNavigate, useLocation } from 'react-router-dom';
import { useCallback } from 'react';
import useJumboAuth from '@jumbo/hooks/useJumboAuth';

/**
 * Hook para navegação inteligente baseada no papel do usuário
 * Redireciona automaticamente para a rota correta baseada no papel
 */
export const useSmartNavigation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { authUser } = useJumboAuth();

  /**
   * Navega para a página de detalhes do processo baseado no papel do usuário
   * @param {string|number} processoId - ID do processo
   */
  const navigateToProcesso = useCallback((processoId) => {
    if (!processoId) {
      return;
    }

    const currentPath = location.pathname;
    let targetRoute;
    
    if (currentPath.includes('/cliente')) {
      targetRoute = `/cliente/processos/${processoId}`;
    } else if (currentPath.includes('/marcas')) {
      targetRoute = `/marcas/processo/${processoId}`;
    } else {
      if (authUser?.role === 'CLIENTE') {
        targetRoute = `/cliente/processos/${processoId}`;
      } else {
        targetRoute = `/marcas/processo/${processoId}`;
      }
    }
    
    navigate(targetRoute);
  }, [navigate, location.pathname, authUser?.role]);

  /**
   * Gera a URL correta para o processo baseado no papel do usuário
   * @param {string|number} processoId - ID do processo
   * @returns {string} - URL do processo
   */
  const getProcessoUrl = (processoId) => {
    if (!processoId) return '#';
    
    const baseRoute = authUser?.role === 'CLIENTE' 
      ? '/cliente/processos' 
      : '/marcas/processo'; // ✅ Correção: era '/marcas/processo' mas deveria ser consistente
    
    return `${baseRoute}/${processoId}`;
  };

  /**
   * Verifica se o usuário tem acesso a uma rota específica
   * @param {string} route - Rota a ser verificada
   * @returns {boolean} - Se o usuário tem acesso
   */
  const hasAccessToRoute = (route) => {
    const userRole = authUser?.role;
    
    // Rotas que apenas CLIENTEs podem acessar
    if (route.startsWith('/cliente/')) {
      return userRole === 'CLIENTE';
    }
    
    // Rotas que apenas OPERADOREs e ADMINs podem acessar
    if (route.startsWith('/marcas/')) {
      return userRole === 'OPERADOR' || userRole === 'ADMIN';
    }
    
    // Rotas que apenas ADMINs podem acessar
    if (route.startsWith('/usuarios') || route.startsWith('/envolvidos') || route.startsWith('/logs-rpis')) {
      return userRole === 'ADMIN';
    }
    
    return true; // Rotas públicas ou dashboard
  };

  /**
   * Navega para uma rota apenas se o usuário tiver acesso
   * @param {string} route - Rota de destino
   * @param {string} fallbackRoute - Rota de fallback se não tiver acesso (padrão: dashboard)
   */
  const safeNavigate = (route, fallbackRoute = '/dashboard') => {
    if (hasAccessToRoute(route)) {
      navigate(route);
    } else {
      navigate(fallbackRoute);
    }
  };

  return {
    navigateToProcesso,
    getProcessoUrl,
    hasAccessToRoute,
    safeNavigate,
    navigate, // Expose o navigate padrão também
    userRole: authUser?.role
  };
};

/**
 * Utilitário para gerar URLs sem hooks (para uso em componentes de classe ou fora de React)
 */
export const generateProcessoUrl = (processoId, userRole) => {
  if (!processoId) return '#';
  
  const baseRoute = userRole === 'CLIENTE' 
    ? '/cliente/processos' 
    : '/marcas/processo';
  
  return `${baseRoute}/${processoId}`;
};
